import React from 'react'

export type DropzoneProps = {
    accept?: string
    multiple?: boolean
    file?: File | null
    onFileSelected: (file: File | null) => void
    disabled?: boolean
    label?: string
}

export default function Dropzone({ accept = '*/*', multiple = false, file, onFileSelected, disabled, label }: DropzoneProps) {
    const inputRef = React.useRef<HTMLInputElement | null>(null)
    const [dragOver, setDragOver] = React.useState(false)

    const onChoose = () => {
        if (disabled) return
        inputRef.current?.click()
    }

    const handleFiles = (files: FileList | null) => {
        if (!files || files.length === 0) return
        const f = files[0]
        onFileSelected(f)
    }

    return (
        <div
            className={`relative rounded-lg border ${dragOver ? 'border-blue-500 ring-1 ring-blue-500/30' : 'border-gray-700'} p-4 text-center bg-gradient-to-br from-gray-900 to-gray-800`}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!disabled) setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (disabled) return; setDragOver(false); handleFiles(e.dataTransfer?.files || null) }}
            role="button"
            tabIndex={0}
            onClick={onChoose}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChoose(); } }}
            aria-disabled={disabled}
        >
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                disabled={disabled}
            />
            {!file ? (
                <>
                    <div className="text-sm text-gray-200">{label || 'Drag & drop a file here, or click to select'}</div>
                    <div className="text-[11px] text-gray-500 mt-1">Supported: {accept || 'Any'}</div>
                </>
            ) : (
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 text-left">
                        <div className="text-xs text-gray-300 truncate" title={file.name}>{file.name}</div>
                        <div className="text-[11px] text-gray-500">{Math.round(file.size / 1024)} KB</div>
                    </div>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onFileSelected(null) }}
                        className="px-2 py-1 text-xs rounded-md border border-gray-700 bg-gray-800 text-gray-200 hover:bg-gray-700"
                    >
                        Change
                    </button>
                </div>
            )}
        </div>
    )
}
